import type { Project } from '../../types/project';

export interface ProjectsState {
    activityProjectsMapping: ActivityProject[];
    projectsList: ProjectsList;
    badges: ProjectsTasksBadges | null;
    activeCount: ActiveCountResponse | null;
    tags: {
        all: TagsList;
        active: TagsList;
        projectsTags: {
            [id: string]: Tag[];
        };
        isUpdating: boolean;
        projectsErrors: {
            [id: string]: boolean;
        };
    };
    isAllProjectsCollapsed: boolean;
    isUpdating: boolean;
    hasError: boolean;
    searchSuggestions: {
        isLoading: boolean;
        results: {
            [objectType: string]: {
                [query: string]: ProjectsMainSearchData;
            };
        };
    };
    filenameError: FilenameError;
    tasksBadge: number;
}

export type ProjectsList = {
    list: Project[];
    page: number;
    hasMore: boolean;
};

export interface ProjectFile {
    format: number;
    name: string;
    id: string;
    stats: {
        wordCount: number;
        wordCountUntranslated: number;
        calcIsFuzzy: number;
        percentDone: number;
        languages: {
            [id: string]: {
                percentDone: number;
                wordCount: number;
                wordCountUntranslated: number;
            };
        };
    };
    title: string | null;
}

export interface Tag {
    contentIntegration: string;
    value: string;
    hash: string | null;
}

export type TagsList = string[];

export interface AllLanguagesLanguage {
    id: string;
    text: string;
}

export interface PostTagsApiResponse {
    hashes: {
        hash: string;
        tag: string;
        contentIntegration: string;
    }[];
    tags: string[];
}

export interface GetTagsApiResponse {
    hashes: {
        hash: string;
        tag: string;
        contentIntegration: string;
    }[];
    tags: string[];
}

export interface GetProjectsListResponse {
    project_list: Project[];
    project_tags: {
        [id: string]: Tag[];
    };
    task_badges: ProjectsTasksBadges;
    hasMore: boolean;
}

export enum ObjectType {
    ALL = 'all',
    PROJECT = 'project',
    KEY = 'key',
    TRANSLATION = 'translation',
    TAG = 'tag',
}

export interface SearchKeyObject {
    branch_name: null | string;
    key_id: string;
    key_name: string;
    project_id: string;
    project_name: string;
}

export interface SearchProjectObject {
    project_id: string;
    project_name: string;
}

export interface SearchTranslationObject {
    branch_name: null | string;
    key_id: string;
    key_name: string;
    language_id: number;
    project_id: string;
    project_name: string;
    rtl: string;
    translation: string;
}

export interface SearchTagObject {
    branch_name: null | string;
    project_id: string;
    project_name: string;
    tag: string;
    tag_hash: string;
}

export interface ProjectsMainSearchData {
    exclude: 0 | 1;
    highlight_query: string;
    object_type: ObjectType;
    query: string;
    keys: {
        items: SearchKeyObject[];
        total: number;
    };
    projects: {
        items: SearchProjectObject[];
        total: number;
    };
    translations: {
        items: SearchTranslationObject[];
        total: number;
    };
    tags: {
        items: SearchTagObject[];
        totalProjects: number;
        projectsOnPage: number;
    };
}

export interface GetProjectsMainSearchResponse {
    err: 0 | 1;
    message?: string;
    data: ProjectsMainSearchData;
}

export interface SetProjectActiveBranchResponse {
    result: string;
}

export interface PostFilenameRenameResponse {
    err: 0 | 1 | 2 | 3;
    filename: string;
    id: string;
    message?: string;
}

export interface FilenameError {
    code: number | null;
    id: string | null;
    name: string | null;
    message: string | null;
}

export interface ProjectsTasksBadgesData {
    insert_id: number;
    name: string;
    task_count: number;
}

export interface ProjectsTasksBadges {
    [projectId: string]: readonly ProjectsTasksBadgesData[];
}

export interface TasksBadgesResponse {
    projects: ProjectsTasksBadges;
    widget: number;
}

export interface ActiveCountResponse {
    [projectId: string]: {
        count: number;
    };
}

export interface ActivityProject {
    id: string;
    branch: string | null;
}

export interface AdminRights {
    upload: boolean;
    download: boolean;
    tasks: boolean;
    contributors: boolean;
    screenshots: boolean;
    manageKeys: boolean;
    manageLanguages: boolean;
    settings: boolean;
    activity: boolean;
    statistics: boolean;
    createBranches: boolean;
}

export enum ProjectViewMode {
    collapse = 'collapse',
    expand = 'expand',
}
